import React, { FC, useCallback } from "react";

import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFlows } from "@frigade/react";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import { GuideStatus } from "@mapmycustomers/shared";
import { Guide, GuideUserStatus } from "@mapmycustomers/shared/types/entity";
import { ButtonLink } from "@mapmycustomers/ui";

import frigadeService from "@app/util/frigade/frigadeService";

import GuideStatusTag from "../../GuideStatusTag";
import GuideTypeTag from "../../GuideTypeTag";

import styles from "./GuideCard.module.scss";

const messages = defineMessages({
  startButton: {
    id: "onboarding.user.card.start",
    defaultMessage: "Start {completed, select, true {again } other {}}{icon}",
    description: "Start button title on guide card on the User Guides page",
  },
});

export interface GuideCardProps {
  className?: string;
  guide: Guide;
  guideStatus: GuideUserStatus;
}

const GuideCard: FC<GuideCardProps> = ({ className, guide, guideStatus }) => {
  const intl = useIntl();

  const { markFlowNotStarted, markFlowStarted } = useFlows();

  const handleStartClick = useCallback(() => {
    if (guide.frigadeFlowId) {
      frigadeService.showFlow(guide.frigadeFlowId);
      if (guideStatus.status === GuideStatus.PROGRESS) {
        markFlowStarted(guide.frigadeFlowId);
      } else {
        markFlowNotStarted(guide.frigadeFlowId);
      }
    }
  }, [guide, guideStatus.status, markFlowNotStarted, markFlowStarted]);

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.header}>
        <div className={styles.title}>{guide.displayName}</div>
        <GuideStatusTag status={guideStatus?.status} />
      </div>
      <div className={styles.info}>
        <GuideTypeTag type={guide.type} />
      </div>
      <div className={styles.description}>{guide.description}</div>
      <div className={styles.buttons}>
        <ButtonLink className={styles.startButton} onClick={handleStartClick}>
          {intl.formatMessage(messages.startButton, {
            completed: guideStatus.status === GuideStatus.COMPLETED,
            icon: <FontAwesomeIcon className={styles.icon} icon={faChevronRight} />,
          })}
        </ButtonLink>
      </div>
    </div>
  );
};

export default GuideCard;
